import React, { lazy } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import ScrollToTop from './scroll'
import Loader from '../components/FallBack/Loader'
import { stripLocale } from '../utils/locale'
import RouteSeo from '../seo/RouteSeo'

// Locale-prefixed URLs (/es/*, /pt/*) that don't have an explicit localized
// route fall back to the English page (strip the prefix). Explicit localized
// routes like /es/mediakit rank above the /es/* splat in React Router v6.
function LocaleRedirect (): JSX.Element {
  const { pathname, search, hash } = useLocation()
  return <Navigate to={stripLocale(pathname) + search + hash} replace />
}

// eslint-disable-next-line @typescript-eslint/space-before-function-paren

const Layout = lazy(() => import('../Layout'))
const LandingPage = lazy(() => import('../pages/Landing'))
const Preview = lazy(() => import('../pages/Public/index'))
const AboutUsPage = lazy(() => import('../pages/AboutUs'))
const CareerPage = lazy(() => import('../pages/Careers'))
const CookiePolicyPage = lazy(() => import('../pages/CookiePolicy'))
const EarningsCalculator = lazy(() => import('../pages/Features/EarningsCalculator'))
const EmailMarketingFeature = lazy(() => import('../pages/Features/EmailMarketing'))
const IncomeDashboardFeature = lazy(() => import('../pages/Features/IncomeDashboard'))
const LinkInBioFeature = lazy(() => import('../pages/Features/LinkInBio'))
const QRCodeFeature = lazy(() => import('../pages/Features/QRCode'))
const PrivacyPolicyPage = lazy(() => import('../pages/PrivacyPolicy'))
const ReportViolation = lazy(() => import('../pages/ReportViolation'))
const TermsConditionsPage = lazy(() => import('../pages/TermsConditions'))
const ClickAnalytics = lazy(() => import('../pages/autoDmAnalytics/ClickAnalytics'))
const CustomRedirect = lazy(() => import('../pages/autoDmAnalytics/CustomRedirect'))
const AutoDM = lazy(() => import('../pages/Features/AutoDM'))
const NotFound = lazy(() => import('../pages/404'))
const AffiliateLinkRedirect = lazy(() => import('../pages/AffiliateLinkRedirect'))
const LibRedirectionLink = lazy(() => import('../pages/Lib'))
const PricingPage = lazy(() => import('../pages/Pricing'))
const SmartLinkPage = lazy(() => import('../pages/SmartLink'))
const SmartLinkLanding = lazy(() => import('../pages/Features/SmartLink'))
const MediaKit = lazy(() => import('../pages/Features/MediaKit'))
const CollabX = lazy(() => import('../pages/Features/CollabX'))
const PublicProfile = lazy(() => import('../components/PublicProfile'))
const BlockList = lazy(() => import('../Layout/linkInBio/BlockList'))
const Ask = lazy(() => import('../pages/Public/Ask'))
const LlmInfo = lazy(() => import('../pages/LlmInfo'))
const AffiliateProgramPage = lazy(() => import('../pages/AffiliateProgram'))
const AmaPage = lazy(() => import('../pages/Ama'))
export default function AppRoute (): JSX.Element {
  return (
    <React.Fragment>
      <ScrollToTop />
      <RouteSeo />
      <React.Suspense fallback={<Loader />}>
        <Routes>
          <Route element={<Layout />}>
            <Route path="*" element={<NotFound />} />
            {/* Retired "Coming Soon" pages. Listed explicitly, otherwise the
                :userName/* profile route would treat them as usernames. */}
            <Route path="/store" element={<NotFound />} />
            <Route path="/generate-ai" element={<NotFound />} />
            <Route path="/influencer-marketing" element={<NotFound />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route
              path="/"
              element={
                <LandingPage />
              }
            />
            <Route
              path="/about-us"
              element={
                <AboutUsPage />
              }
            />
            <Route
              path="/report-violation"
              element={
                <ReportViolation />
              }
            />
            <Route
              path="/careers"
              element={
                <CareerPage />
              }
            />
            <Route
              path="/privacy-policy"
              element={
                <PrivacyPolicyPage />
              }
            />
            <Route
              path="/cookie-policy"
              element={
                <CookiePolicyPage />
              }
            />
            <Route path='pricing/*' element={<PricingPage />} />
            {/* Renders its own top bar; Layout skips the site Header on this path. */}
            <Route path="/affiliate-program" element={<AffiliateProgramPage />} />
            <Route path="/ama" element={<AmaPage />} />
            <Route
              path="/terms-and-conditions"
              element={
                <TermsConditionsPage />
              }
            />
            <Route
              path="/link-in-bio"
              element={
                <LinkInBioFeature />
              }
            />
            <Route
              path="/qr-code-generator"
              element={
                <QRCodeFeature />
              }
            />
            <Route
              path="/income-dashboard"
              element={
                <IncomeDashboardFeature />
              }
            />
            <Route
              path="/email-marketing"
              element={
                <EmailMarketingFeature />
              }
            />
            {/* Link Shortener page retired — its SEO is being migrated to the
                SmartLink landing page. Keep this redirect until rankings move. */}
            <Route path="/link-shortener" element={<Navigate to="/smart-link" replace />} />
            <Route
              path="/earnings-calculator"
              element={
                <EarningsCalculator />
              }
            />
            <Route path='/autoDM'
              element={
                <AutoDM />
              } />
            {/* Redirects from old /features/* paths to new top-level paths */}
            <Route path="/features/link-in-bio" element={<Navigate to="/link-in-bio" replace />} />
            <Route path="/features/qr-code-generator" element={<Navigate to="/qr-code-generator" replace />} />
            <Route path="/features/income-dashboard" element={<Navigate to="/income-dashboard" replace />} />
            <Route path="/features/generate-ai" element={<Navigate to="/generate-ai" replace />} />
            <Route path="/features/email-marketing" element={<Navigate to="/email-marketing" replace />} />
            <Route path="/features/store" element={<Navigate to="/store" replace />} />
            <Route path="/features/link-shortener" element={<Navigate to="/smart-link" replace />} />
            <Route path="/features/influencer-marketing" element={<Navigate to="/influencer-marketing" replace />} />
            <Route path="/features/earnings-calculator" element={<Navigate to="/earnings-calculator" replace />} />
            <Route path="/features/autoDM" element={<Navigate to="/autoDM" replace />} />
          </Route>
          <Route path="/mediakit" element={<MediaKit lang="en" />} />
          <Route path="/pt/mediakit" element={<MediaKit lang="pt" />} />
          <Route path="/es/mediakit" element={<MediaKit lang="es" />} />
          <Route path="/smart-link" element={<SmartLinkLanding />} />
          {/* GEO / llms.txt page — raw markdown fact sheet for AI crawlers.
              Standalone (no site chrome) so the feed stays close to plain markdown. */}
          <Route path="/llm-info" element={<LlmInfo />} />
          <Route path="/collabx" element={<CollabX lang="en" />} />
          <Route path="/pt/collabx" element={<CollabX lang="pt" />} />
          <Route path="/es/collabx" element={<CollabX lang="es" />} />
          {/* Un-built localized pages fall back to their English equivalent */}
          <Route path="/es/*" element={<LocaleRedirect />} />
          <Route path="/pt/*" element={<LocaleRedirect />} />
          <Route path="/features/mediakit" element={<Navigate to="/mediakit" replace />} />
          <Route path="/features/collabx" element={<Navigate to="/collabx" replace />} />
          <Route path='/dm' element={<ClickAnalytics />} />
          <Route path="/:username/i/:code" element={<SmartLinkPage />} />
          <Route path="/i/:code" element={<SmartLinkPage />} />
          <Route path='/p/:code' element={<AffiliateLinkRedirect />} />
          <Route path='/lib' element={<LibRedirectionLink />} />
          <Route path="/wa" element={<CustomRedirect />} />
          <Route path="/:username/mediakit" element={<PublicProfile />} />
          <Route path=":userName/*" element={<Preview />}>
            <Route index element={<BlockList />} />
            <Route path='ask' element={<Ask />} />
            <Route path=':pageId' element={<BlockList />} />
          </Route>
        </Routes>
      </React.Suspense>
    </React.Fragment>
  )
}
